/**
 * Copyright 2026 Ziz Academy
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Exam, ExamSubmission, UserRole } from '../types';
import { examApi, submissionApi } from '../services/api';

// Simple status filter type
type EvalFilter = 'PENDING' | 'COMPLETED' | 'BOTH';

const Evaluations: React.FC = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [submissions, setSubmissions] = useState<ExamSubmission[]>([]);
    const [filter, setFilter] = useState<EvalFilter>('PENDING');

    const role: UserRole | undefined = user?.role as any;

    useEffect(() => {
        const load = async () => {
            if (!user) return;
            setLoading(true);
            setError(null);
            try {
                if (role === 'TEACHER') {
                    // Load exams created by teacher, then gather all submissions across those exams
                    const exams: Exam[] = await examApi.getByTeacher(user.id);
                    const allSubsArrays = await Promise.all(
                        (exams || []).map(ex => submissionApi.getByExam(ex.id!))
                    );
                    const flat = ([] as ExamSubmission[]).concat(...allSubsArrays);
                    // Attach exam title when missing
                    const examTitleMap = new Map<number, string>();
                    exams.forEach(ex => { if (ex.id) examTitleMap.set(ex.id, ex.title); });
                    const enriched = flat.map(s => ({ ...s, examTitle: s.examTitle || examTitleMap.get(s.examId) }));
                    setSubmissions(enriched);
                } else if (role === 'ADMIN' || role === 'SUPER_ADMIN') {
                    // Admin: use existing admin endpoint; backend should scope to admin's institution if applicable
                    const adminSubs = await submissionApi.getAllSubmissionsForAdmin();
                    setSubmissions(adminSubs || []);
                } else {
                    setSubmissions([]);
                }
            } catch (e) {
                console.error('Failed to load evaluations', e);
                setError('Failed to load evaluations.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [user?.id, role]);

    const filtered = useMemo(() => {
        if (filter === 'BOTH') return submissions;
        const want = filter === 'COMPLETED';
        return submissions.filter(s => !!s.isEvaluated === want);
    }, [submissions, filter]);

    const pendingCount = useMemo(() => submissions.filter(s => !s.isEvaluated).length, [submissions]);
    const completedCount = useMemo(() => submissions.filter(s => !!s.isEvaluated).length, [submissions]);

    return (
        <div data-testid="evaluations-page">
            <div className="card" style={{ background: 'linear-gradient(120deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>
                    🧮 Evaluations
                </h1>
                <p style={{ opacity: 0.9 }}>
                    {role === 'TEACHER' && 'See submissions awaiting your evaluation.'}
                    {(role === 'ADMIN' || role === 'SUPER_ADMIN') && "See your institution's submissions."}
                </p>
            </div>

            {/* Filters */}
            <div className="card" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <strong>Show:</strong>
                <button
                    type="button"
                    className={`btn btn-sm ${filter === 'PENDING' ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setFilter('PENDING')}
                >
                    ⌛ Pending ({pendingCount})
                </button>
                <button
                    type="button"
                    className={`btn btn-sm ${filter === 'COMPLETED' ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setFilter('COMPLETED')}
                >
                    ✅ Completed ({completedCount})
                </button>
                <button
                    type="button"
                    className={`btn btn-sm ${filter === 'BOTH' ? 'btn-primary' : 'btn-outline'}`}
                    onClick={() => setFilter('BOTH')}
                >
                    📄 Both ({submissions.length})
                </button>
            </div>

            {loading && (
                <div className="loading"><div className="spinner"></div></div>
            )}

            {error && (
                <div className="alert alert-error">{error}</div>
            )}

            {!loading && filtered.length === 0 && (
                <div className="empty-state">
                    <div className="empty-state-emoji">📭</div>
                    <h3 className="empty-state-title">No submissions found</h3>
                    <p>Try switching the filter to view different items.</p>
                </div>
            )}

            {!loading && filtered.length > 0 && (
                <div className="grid grid-2">
                    {filtered.map((s) => (
                        <div key={s.id} className="card" style={{ borderWidth: '3px', borderStyle: 'solid', borderColor: s.isEvaluated ? '#38ef7d' : '#ffb020' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <div style={{ fontWeight: 700 }}>{s.examTitle || `Exam #${s.examId}`}</div>
                                    <div style={{ fontSize: '0.9rem', color: '#718096' }}>
                                        👤 {s.studentName || `Student #${s.studentId}`}
                                        <span style={{ marginLeft: 12 }}>🕒 {s.submittedAt ? new Date(s.submittedAt).toLocaleString() : 'N/A'}</span>
                                    </div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontWeight: 'bold' }}>{s.totalScore}/{s.totalMarks}</div>
                                    {s.isEvaluated ? (
                                        <span className="badge badge-success">Evaluated</span>
                                    ) : (
                                        <span className="badge badge-warning">Pending</span>
                                    )}
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                                <Link className="btn btn-outline btn-sm" to={`/evaluate/${s.examId}`}>🔎 Open Exam</Link>
                                {!s.isEvaluated && (
                                    <Link className="btn btn-primary btn-sm" to={`/evaluate/${s.examId}`}>✅ Evaluate</Link>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Evaluations;
